import { createNoise2D } from "simplex-noise";

// Deterministic noise from a numeric seed (mulberry32 prng).
function mulberry32(seed: number) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6D2B79F5) | 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export class TerrainSampler {
  private base: (x: number, y: number) => number;
  private hi:   (x: number, y: number) => number;
  private warp: (x: number, y: number) => number;
  private town: (x: number, y: number) => number;

  constructor(public seed: number) {
    const r = mulberry32(seed);
    this.base = createNoise2D(r);
    this.hi   = createNoise2D(r);
    this.warp = createNoise2D(r);
    this.town = createNoise2D(r);
  }

  // Returns terrain height at world (x, z). Deterministic.
  height(x: number, z: number, maxHeight: number): number {
    // Domain warp
    const wx = x + this.warp(x * 0.0008, z * 0.0008) * 80;
    const wz = z + this.warp(x * 0.0008 + 100, z * 0.0008 + 100) * 80;
    let n = 0;
    n += this.base(wx * 0.0009, wz * 0.0009) * 0.55;
    n += this.base(wx * 0.0024, wz * 0.0024) * 0.25;
    n += this.hi  (wx * 0.0070, wz * 0.0070) * 0.13;
    n += this.hi  (wx * 0.0180, wz * 0.0180) * 0.07;
    // shape: pull below sea more than above (more ocean), peak the highs
    const shaped = Math.sign(n) * Math.pow(Math.abs(n), 0.85);
    const h = (shaped * 0.5 + 0.5) * maxHeight - maxHeight * 0.18;
    return h;
  }

  // 0..1 town density. Towns appear on flatter low-altitude land.
  townMask(x: number, z: number, h: number, seaLevel: number): number {
    if (h < seaLevel + 1.5 || h > seaLevel + 30) return 0;
    const t = this.town(x * 0.003, z * 0.003);
    const t2 = this.town(x * 0.011 + 50, z * 0.011 + 50);
    const v = (t * 0.7 + t2 * 0.3 + 1) / 2;
    return v > 0.78 ? (v - 0.78) / 0.22 : 0;
  }
}
